import { editArticle, useFullArticle } from "@/api";
import ArticleForm from "@/components/communication/ArticleForm";
import PageBackButton from "@/components/PageBackButton";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";
import { useSnackbarStore } from "@/store/zustandStore";
import {
  Article,
  ArticleStatus,
  ArticleSubmitType,
  NotificationType,
} from "@/types";
import { Breadcrumbs } from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_portal/building/communication/$articleId/edit")(
  {
    component: RouteComponent,
  },
);

function RouteComponent() {
  const { articleId } = Route.useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useSnackbarStore();
  const { hasCommunication } = useWelcomeCommunicationAccess();
  const { allowBuildingAdminOrReceptionistAccess } =
    useBuildingAdminOrReceptionistAccess();

  const { data: article } = useFullArticle(articleId);
  const articleIsPublished = article?.status === ArticleStatus.PUBLISHED;
  const TRANSLATION_KEY = articleIsPublished
    ? "EDIT_PUBLISHED"
    : "EDIT_UNPUBLISHED";

  const breadcrumbs = [
    {
      label: t("COMMUNICATION.HEADER"),
      path: `/building/communication`,
    },
    {
      label: article.title[i18n.language as "en" | "no"],
      path: `/building/communication/${articleId}`,
    },
    {
      label: t("ARTICLE.ACTIONS.EDIT"),
      path: `/building/communication/${articleId}/edit`,
    },
  ];

  const handleSaveChanges = async (values: ArticleSubmitType) => {
    try {
      await editArticle(articleId, values as Partial<Article>);
      addNotification(
        t(
          `ARTICLE.NOTIFICATION_MESSAGES.${articleIsPublished ? "SAVE_CHANGES" : "SAVE_DRAFT"}_SUCCESS`,
        ),
        NotificationType.SUCCESS,
      );
      navigate({
        to: `/building/communication/$articleId`,
        params: { articleId },
      });
    } catch (error) {
      console.error(error);
      addNotification(
        t(
          `ARTICLE.NOTIFICATION_MESSAGES.${articleIsPublished ? "SAVE_CHANGES" : "SAVE_DRAFT"}_ERROR`,
        ),
      );
    }
  };

  if (!hasCommunication || !allowBuildingAdminOrReceptionistAccess) {
    return null;
  }

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/communication",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t(`PAGES.NEWS.${TRANSLATION_KEY}.HEADER`)}</h1>
        <p>{t(`PAGES.NEWS.${TRANSLATION_KEY}.SUBHEADER`)}</p>
      </div>
      <ArticleForm article={article} onSave={handleSaveChanges} />
    </>
  );
}
